import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import type { SocialMediaCarouselItem } from 'types/capi/modules';

import ChevronIcon from '~/assets/icons/Chevron';

import Slide from './Slide';

type Props = {
  slides: SocialMediaCarouselItem[];
};

const AUTO_SLIDE_DURATION = 2000;

export default function Carousel({ slides }: Props) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'center',
    dragFree: false,
    loop: true,
    slidesToScroll: 1
  });
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;

    const selectedSlide = emblaApi.selectedScrollSnap();

    setCurrentSlideIndex(selectedSlide);
  }, [emblaApi]);

  const onUnfocusedClick = useCallback(
    (index: number) => {
      if (!emblaApi) return;

      setCurrentSlideIndex(index);
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;

    const timer = setTimeout(() => {
      emblaApi.scrollNext();
    }, AUTO_SLIDE_DURATION);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, currentSlideIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative">
      <div className="absolute inset-y-1/2 z-10 flex w-full items-center justify-between px-4">
        <button
          onClick={scrollPrev}
          aria-label="Previous slide"
          className="rotate-90 rounded-full bg-black/20 p-2 hover:bg-black/40"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
        <button
          onClick={scrollNext}
          aria-label="Next slide"
          className="rotate-270 rounded-full bg-black/20 p-2 hover:bg-black/40"
        >
          <ChevronIcon height="16" width="16" stroke="white" />
        </button>
      </div>

      <div className="overflow-hidden py-4" ref={emblaRef}>
        <div className="flex items-center">
          {slides.map((slide, index) => (
            <div
              key={`${index}-${slide.business}-${slide.source}`}
              className="flex flex-none"
            >
              <Slide
                slide={slide}
                isCurrent={index === currentSlideIndex}
                onUnfocusedClick={
                  currentSlideIndex === index
                    ? undefined
                    : () => onUnfocusedClick(index)
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
